import React, { useCallback, useState } from 'react';
import { styled } from '@mui/material';
import Button from '@mui/material/Button';
import Popover from '@mui/material/Popover';
import LogoutIcon from '@mui/icons-material/Logout';
import Avatar from '@/components/Avatar';
import Messages from '@/components/Messages';
import { authentication } from '@/actions';
import { RootState, useAppSelector } from '@/state/store';
import { createSelector } from '@reduxjs/toolkit';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Logo from '@/admin/view/components/icons/Logo';
import ProjectsIcon from '@/components/icons/Projects';
import EmployeesIcon from '@/components/icons/Employees';
import NavButton from './NavButton';
import Notifications from '@/pages/Notifications';
import t from '@/utils/translate';
import PageTitle from '@/components/PageTitle';

type Props = { children: any, header?: any };

const adminTitle = import.meta.env.VITE_ADMIN_PAGE_TITLE || 'Admin section';

const Header = styled('header')({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  display: 'flex',
  justifyContent: 'space-between',
  flexWrap: 'nowrap',
  alignItems: 'center',
  height: '60px',
  padding: '0px 20px',
  boxShadow: '0px 2px 3px rgba(0, 0, 0, 0.04)',
  zIndex: 1100,
  width: '100%',
  gap: '20px',
  backgroundColor: '#091E42',
  color: '#FFFFFF',
});

const HeaderCenter = styled('div')({
  flexGrow: 1,
  textAlign: 'center',
});

const LogoLink = styled(Link)({
  display: 'block',
  paddingRight: '20px',
  borderRight: '1px solid #6B778C',
});

const AppName = styled('div')({
  color: '#FFFFFF',
  fontSize: '17px',
  fontWeight: 500,
});

const NavBar = styled('nav')({
  position: 'fixed',
  top: '60px',
  left: 0,
  right: 0,
  display: 'flex',
  justifyContent: 'flex-start',
  flexWrap: 'nowrap',
  alignItems: 'center',
  height: '52px',
  padding: '10px 20px',
  zIndex: 1050,
  width: '100%',
  background: '#FAFAFA',
});

const Main = styled('main')({
  position: 'fixed',
  top: '112px',
  bottom: 0,
  backgroundColor: '#ffffff',
  overflow: 'auto',
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
  padding: '20px',
});

const UserMenuAnchor = styled(Avatar)({
  cursor: 'pointer',
  width: '32px',
  height: '32px',
  fontSize: '1.2rem',
  lineHeight: 'normal'
});

const UserMenu = styled('div')(({ theme }) => ({
  width: theme.spacing(50),
  padding: theme.spacing(3)
}));

const MenuButton = styled(Button)`
  justify-content: left;
`;

export default ({ children, header }: Props) => {
  const { profile, avatars } = useAppSelector(
    createSelector(
      [
        (state: RootState) => state.authentication.profile,
        (state: RootState) => state.user.avatars,
      ],
      (profile, avatars) => ({ profile, avatars })
    )
  );

  const nav = useNavigate();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const toDashboards = useCallback(() => nav('/admin/dashboards'), []);
  const toProjects = useCallback(() => nav('/admin/projects'), []);
  const toUsers = useCallback(() => nav('/admin/users'), []);
  const toClaims = useCallback(() => nav('/admin/claims'), []);

  return (
    <>
      <PageTitle>{adminTitle}</PageTitle>
      <Header>
        <LogoLink to="/">
          <Logo style={{ fill: '#FFFFFF' }}/>
        </LogoLink>
        <AppName>Admin</AppName>

        <HeaderCenter>{ header }</HeaderCenter>

        <Notifications adminStyle={true}/>

        <UserMenuAnchor
          source={profile && avatars[profile.id]?.source}
          profile={profile}
          onClick={e => setAnchorEl(e.currentTarget)}
        />
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <UserMenu>
            <Link to='/profile'>
              <MenuButton>
                {t('navigation.title.profile')}
              </MenuButton>
            </Link>
            <Link to='/projects'>
              <MenuButton>
                {t('navigation.title.projects')}
              </MenuButton>
            </Link>
            <Link to='/'>
              <MenuButton
                onClick={() => authentication.logout()}
                startIcon={<LogoutIcon />}
              >
                {t('navigation.title.exit')}
              </MenuButton>
            </Link>
          </UserMenu>
        </Popover>
      </Header>

      <NavBar>
        <NavButton
          active={/^\/admin\/dashboards/.test(location.pathname)}
          onClick={toDashboards}
        >
          {t('admin.navigation.title.dashboards')}
        </NavButton>
        <NavButton
          active={/^\/admin\/projects/.test(location.pathname)}
          icon={<ProjectsIcon size={18}/>}
          onClick={toProjects}
        >
          {t('admin.navigation.title.projects')}
        </NavButton>
        <NavButton
          active={/^\/admin\/users/.test(location.pathname)}
          icon={<EmployeesIcon size={18}/>}
          onClick={toUsers}
        >
          {t('admin.navigation.title.users')}
        </NavButton>
        <NavButton
          active={/^\/admin\/claims/.test(location.pathname)}
          onClick={toClaims}
        >
          claim-ы
        </NavButton>
      </NavBar>

      <Main style={{ width: '100%' }}>
        {children}
      </Main>

      <footer>
        <Messages />
      </footer>
    </>
  );
};
